import type React from "react";

import { getSessionsMetadataOptions, SortDirection_api, SessionSortBy_api, type SessionMetadata_api } from "@api";
import { GuiState, useSetGuiState } from "@framework/GuiMessageBroker";
import { buildWorkbenchUrl } from "@framework/internal/WorkbenchSession/utils/url";
import type { Workbench } from "@framework/Workbench";
import { timeAgo } from "@lib/utils/dates";

import { ItemCard } from "./itemCard";
import { RecentList } from "./recentList";

export type RecentSessionsProps = {
    workbench: Workbench;
    className?: string;
};

export function RecentSessions(props: RecentSessionsProps) {
    const setShowOverviewDialog = useSetGuiState(
        props.workbench.getGuiMessageBroker(),
        GuiState.SessionSnapshotOverviewDialogOpen,
    );
    const setOverviewContentMode = useSetGuiState(
        props.workbench.getGuiMessageBroker(),
        GuiState.SessionSnapshotOverviewDialogMode,
    );

    function handleMoreClick() {
        setOverviewContentMode("sessions");
        setShowOverviewDialog(true);
    }

    async function handleSessionClick(sessionId: string, evt: React.MouseEvent) {
        evt.preventDefault();
        await props.workbench.getSessionManager().openSession(sessionId);
    }

    return (
        <RecentList
            className={props.className}
            title="Recent sessions"
            useQueryOptions={{
                ...getSessionsMetadataOptions({
                    query: {
                        sort_by: SessionSortBy_api.METADATA_UPDATED_AT,
                        sort_direction: SortDirection_api.DESC,
                        page_size: 5,
                    },
                }),
            }}
            onDialogIconClick={handleMoreClick}
            transformData={(data) => data.items}
            renderItem={(item: SessionMetadata_api) => (
                <ItemCard
                    href={buildWorkbenchUrl({ kind: "session", sessionId: item.id, dashboardId: null })}
                    onClick={handleSessionClick}
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    timestamp={item.updatedAt}
                    description={item.description}
                    tooltipInfo={{
                        Created: timeAgo(Date.now() - new Date(item.createdAt ?? "").getTime()),
                        Updated: timeAgo(Date.now() - new Date(item.updatedAt ?? "").getTime()),
                    }}
                />
            )}
            makeItemKey={(item: SessionMetadata_api) => item.id}
        />
    );
}
